// Auto-generado por @xone-web/staticweb v0.13.0 (MPA Island mode).
// Pagina: LoginColl
// NO EDITAR: se regenera en cada build.

import { createAppContext } from "@xone-web/app-orchestrator";
import { registerXoneIsland, registerXoneCombo, installCanvasSizerFromDocument, hydrateVisibleContentsNow } from "@xone-web/islands-runtime";
import * as events from "./LoginColl.events";
import * as __functions from "./functions";
import { ROUTE_MAP } from "./routes";
import { APP_CONFIG } from "./app-config";
import { COLL_DEF } from "./LoginColl.colldef";


const api = createAppContext({
    routeMap: ROUTE_MAP,
    apiBase: APP_CONFIG.apiBase,
    backend: (APP_CONFIG as { backend?: "webcore" | "generated" }).backend ?? "generated",
    tablePrefix: APP_CONFIG.tablePrefix,
    appName: APP_CONFIG.name,
    appVersion: APP_CONFIG.version,
    projectId: APP_CONFIG.projectId,
    collectionName: "LoginColl",
    collectionDefinition: COLL_DEF as unknown as Record<string, unknown>,
});

// Bake entry-point y login collection para navegacion post-login
api.macros.set("ENTRY_POINT", APP_CONFIG.entryPoint);
if (APP_CONFIG.loginCollection) {
    api.macros.set("LOGIN_COLLECTION", APP_CONFIG.loginCollection);
}

// Registrar objetos API XOne como globals en window para que
// functions.js pueda acceder a ui, self, appData, $http, crypto,
// console como variables globales (paridad sandbox XOne).
const __w = window as unknown as Record<string, unknown>;
__w.ui = api.ui;
__w.self = api.self;
__w.This = api.This;
__w.ThisDataColl = api.ThisDataColl;
__w.appData = api.appData;
__w.$http = api.$http;
// window.crypto is read-only (Web Crypto API) — use xoneCrypto alias
Object.defineProperty(__w, "xoneCrypto", { value: api.crypto, configurable: true });
try { Object.defineProperty(__w, "crypto", { value: api.crypto, configurable: true }); } catch { /* read-only in browser */ }
// clipboard: portapapeles del sistema (paridad objeto de script XOne `Clipboard`).
// Los proyectos lo usan como global pelado, p.ej. `clipboard.setText(campo)`.
__w.clipboard = api.clipboard;
__w.Clipboard = api.clipboard;
__w.Bundle = api.Bundle;
__w.console = api.console;
__w.user = api.appData.getCurrentUser() ?? {};

// `getContents(name)` global: en XOne un `getContents` pelado dentro de una
// función global de functions.js (sin receptor) opera sobre el objeto/página
// en curso. En el MPA ese objeto es `self` (la página), cuyos <contents>
// incluyen los del layout-shell fusionados (p.ej. WebMenuPadre/WebMenuHijo).
// Paridad Android `XoneDataObject.getContents`; síncrono (devuelve el wrapper
// compartido o null si el <contents> no está declarado).
__w.getContents = (nameOrIndex) => {
    const s = api.self;
    return s && typeof s.getContents === "function" ? s.getContents(nameOrIndex) : null;
};

// Registrar funciones globales de functions.js en window para que
// los eventos transpilados puedan llamarlas como bare identifiers.
for (const [k, v] of Object.entries(__functions)) {
    if (typeof v === "function") __w[k] = v;
}

registerXoneIsland({ api, events: events });
registerXoneCombo();

// Sizer responsive (F1 §1.A): fija --xone-p-unit/--xone-font-unit en px sobre
// el .xone-canvas para desacoplarlas de cqw, permitiendo containerizar frames
// sin romper el escalado 'p'. No-op en proyectos legacy (sin --xone-base-width).
// Se instala ASAP (módulo deferred = DOM ya parseado) para minimizar FOUC.
installCanvasSizerFromDocument();

const handlers = events as Record<string, ((api: unknown, ...args: unknown[]) => Promise<unknown>) | undefined>;
// self global del modelo XOne: las funciones globales del proyecto referencian
// 'self' (el objeto del evento actual). Lo fijamos antes de cada handler de
// lifecycle = el self de la pagina.
const __setXoneSelf = (__functions as Record<string, unknown>)["__xoneSetSelf"] as ((v: unknown) => void) | undefined;
async function invoke(name: string): Promise<void> {
    const fn = handlers[name];
    if (typeof fn === "function") {
        __setXoneSelf?.((api as { self?: unknown }).self);
        await fn(api);
    }
}


// Difiere la carga del iframe externo de un vendor widget: lo deja en
// about:blank tras montar y restaura su src real la 1a vez que el usuario
// interactua con el tray. Evita que los redirects del iframe (login/oauth)
// contaminen la history del navegador y rompan history.back()/ui.back().
function deferVendorIframe(iframeSel: string, traySel: string): void {
    const frames = Array.from(document.querySelectorAll(iframeSel)) as HTMLIFrameElement[];
    if (frames.length === 0) return;
    for (const fr of frames) {
        if (fr.src && fr.src !== "about:blank") {
            fr.dataset["xoneDeferSrc"] = fr.src;
            fr.src = "about:blank";
        }
    }
    const tray = document.querySelector(traySel);
    if (!tray) return;
    tray.addEventListener("pointerdown", () => {
        for (const fr of Array.from(document.querySelectorAll(iframeSel)) as HTMLIFrameElement[]) {
            const src = fr.dataset["xoneDeferSrc"];
            if (src) { fr.src = src; delete fr.dataset["xoneDeferSrc"]; }
        }
    }, { once: true, capture: true });
}

document.addEventListener("DOMContentLoaded", async () => {
    // Ciclo de vida XOne (paridad EditViewHyper.java):
    //   load (onload, objeto traído del repositorio) → create (al crearlo) →
    //   before-edit (ANTES de renderizar) → [render: se abre el gate y los
    //   <contents> rendican] → after-edit (UNA VEZ finalizado el render).
    // Android dispara after-edit "después de pintar toda la pantalla"
    // (EditViewHyper.java:2706 doLoadTabItemsFinish → sendAfterEditMessage),
    // por eso after-edit corre DESPUÉS de signalLifecycleReady. before-edit va
    // antes (puede fijar el filtro del contents).
    try {
        await invoke("load");
        await invoke("create");
        await invoke("beforeEdit");
    } catch (err) {
        console.error("[xone] before-edit lifecycle error in LoginColl:", err);
    } finally {
        // Abrir el gate → arranca el render de los <contents>.
        api.signalLifecycleReady?.();
        // Prefetch coordinado: hidrata los <contents> YA visibles en un solo
        // bucle síncrono (antes del primer paint, ganando al IO por-isla) → sus
        // loadAll caen en la misma ráfaga y el coalescer del cliente los funde en
        // un único POST /DoBatch en vez de N peticiones en cascada. NO toca los
        // contents ocultos (tabs/paneles/floating) ni los de debajo del fold:
        // esos siguen difiriéndose. Idempotente y best-effort.
        try { hydrateVisibleContentsNow(); } catch (err) {
            console.error("[xone] hydrateVisibleContentsNow error:", err);
        }
    }
    // after-edit: tras disparar el render. Los controles estáticos (botones/
    // inputs/labels) ya están en el DOM desde DOMContentLoaded, así que
    // setFieldPropertyValue (p.ej. el title de BTVERSION) se refleja; el sync
    // final propaga los valores calculados a spans/inputs.
    try {
        await invoke("afterEdit");
        api.domSync.syncAll();
    } catch (err) {
        console.error("[xone] after-edit lifecycle error in LoginColl:", err);
    }
    
    // Vendor DOM widgets: re-montar por-página tras auth (paridad SPA prod).
    // Gate BACKEND-AGNOSTICO: montar en toda pagina que NO sea la de login. Las
    // paginas no-login solo se alcanzan tras un login correcto (el flujo redirige
    // a login si no hay sesion), asi que "no estas en login" implica autenticado.
    // NO usamos api.session.isAuthenticated(): solo refleja el token del backend
    // REST (sessionStorage xone:auth) y da false con el backend WebCore (auth via
    // /api/itf/Auth) -> el widget nunca montaria en ese variante. Montar para un
    // hipotetico deep-link anonimo es inocuo (el iframe del SDK muestra su propio
    // login). Excluir la coleccion de login evita pintarlo antes de auth.
    try {
        const __isLogin = APP_CONFIG.loginCollection === "LoginColl";
        const __w2 = window as unknown as Record<string, unknown>;
        if (!__isLogin) {
            // Ringover
            if (!document.querySelector("[id^=\"ringover-tray-\"]") && typeof __w2["iniciarRingover"] === "function") {
                (__w2["iniciarRingover"] as () => void)();
            }
            // Carga diferida del iframe externo: sus redirects (login/oauth)
            // contaminan la history del navegador y romperian history.back()/
            // ui.back() (botones "Volver"). Lo dejamos en about:blank y
            // restauramos su src real la 1a vez que el usuario abre el widget.
            deferVendorIframe("iframe[id^=\"ringover-iframe-\"]", "[id^=\"ringover-tray-\"]");
        }
    } catch (err) {
        console.error("[xone] vendor DOM widget init error:", err);
    }

    // Reset dirty AFTER lifecycle + island hydration. setTimeout(0) ensures
    // any pending microtasks from island bind events complete first.
    // Only then register the beforeunload guard.
    setTimeout(() => {
        const s = api.self as unknown as { _dirty?: { setOriginal?: (f: Map<string, unknown>) => void } };
        if (s._dirty?.setOriginal) {
            s._dirty.setOriginal(new Map(Object.entries(api.self.toJson())));
        }
        window.addEventListener("beforeunload", (e) => {
            // Navegación programática XOne (openCollection, redirect post-login,
            // back): NO avisar — paridad Android. Además el diálogo durante una
            // View Transition cross-document congela el rendering de la página
            // destino (rAF/IO suspendidos, islands sin hidratar).
            const w = window as unknown as Record<string, unknown>;
            if (w.__xoneProgrammaticNav === true) return;
            if (typeof api.self.getDirty === "function" && api.self.getDirty()) {
                e.preventDefault();
            }
        });
    }, 0);
});

// bfcache pageshow handler ya registrado por createAppContext —
// no duplicar aqui.

if (typeof window !== "undefined") {
    (window as unknown as Record<string, unknown>).__xone = { collection: "LoginColl", api, events };
}
